import type { PlaytimeResponse, PlaytimeWeek } from '@brimstone/shared';
import { DataGrid, DataRow, EmptyState, PageLayout, Panel } from '../../components/ui';
import { formatPlayed } from '../../lib/time';
import { usePlaytime } from '../../lib/tracking';

const WEEKS = 8;

/** "18 Nov": the reset date, in UTC like the week id. */
function weekLabel(w: PlaytimeWeek): string {
  return new Date(w.startAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

const hours = (seconds: number) => (seconds ? formatPlayed(seconds) : '—');

/** One small multiple: a bar per week, scaled to `max`, all in the accent. */
function Bars({
  seconds,
  max,
  weeks,
  who,
}: {
  seconds: number[];
  max: number;
  weeks: PlaytimeWeek[];
  who: string;
}) {
  return (
    <>
      {seconds.map((s, i) => (
        <span
          key={weeks[i]!.id}
          role="cell"
          title={`${who} · week of ${weekLabel(weeks[i]!)}: ${hours(s)}`}
          className="flex h-10 items-end border-b border-line"
        >
          <span
            className="block w-full bg-accent"
            style={{ height: max ? `${(s / max) * 100}%` : 0 }}
          />
        </span>
      ))}
    </>
  );
}

function Grid({ data }: { data: PlaytimeResponse }) {
  const columns = `120px repeat(${data.weeks.length}, minmax(0, 1fr)) 72px`;
  // Characters share one scale so their rows compare; the total row has its own.
  const max = Math.max(0, ...data.characters.flatMap((c) => c.seconds));
  const totalMax = Math.max(0, ...data.totals);
  return (
    <DataGrid
      label="Playtime per week"
      columns={columns}
      header={['Character', ...data.weeks.map(weekLabel), 'Total']}
    >
      {data.characters.map((c) => (
        <DataRow key={c.id} columns={columns}>
          <span role="cell" className="truncate text-name">
            {c.name}
          </span>
          <Bars seconds={c.seconds} max={max} weeks={data.weeks} who={c.name} />
          <span role="cell" className="text-right font-mono text-secondary">
            {hours(c.totalSeconds)}
          </span>
        </DataRow>
      ))}
      <DataRow columns={columns}>
        <span role="cell" className="text-name font-semibold">
          Total
        </span>
        <Bars seconds={data.totals} max={totalMax} weeks={data.weeks} who="Everyone" />
        <span role="cell" className="text-right font-mono text-secondary">
          {hours(data.totals.reduce((a, b) => a + b, 0))}
        </span>
      </DataRow>
    </DataGrid>
  );
}

function ThisWeek({ data }: { data: PlaytimeResponse }) {
  const last = data.weeks.length - 1;
  const rows = data.characters
    .map((c) => ({ id: c.id, name: c.name, seconds: c.seconds[last] ?? 0 }))
    .filter((r) => r.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds);
  return (
    <Panel title="This week" level="side" meta={hours(data.totals[last] ?? 0)}>
      {rows.length === 0 ? (
        <EmptyState>Nobody has played since the reset.</EmptyState>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 px-4 py-3 text-compact">
          {rows.map((r) => (
            <li key={r.id} className="flex justify-between gap-3">
              <span>{r.name}</span>
              <span className="font-mono text-muted">{hours(r.seconds)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** /playtime (SPEC §6.8): session time per character per reset week, last 8 weeks. */
export function PlaytimePage() {
  const q = usePlaytime(WEEKS);
  const data = q.data;
  return (
    <PageLayout
      main={
        <Panel title="Playtime" meta={`LAST ${WEEKS} WEEKS`}>
          {!data ? (
            <EmptyState>{q.isError ? 'Couldn’t load playtime.' : 'Loading…'}</EmptyState>
          ) : !data.hasData ? (
            <EmptyState>
              No sessions yet — the Brimstone addon records them from the next login.
            </EmptyState>
          ) : data.characters.length === 0 ? (
            <EmptyState>Nobody played in these {WEEKS} weeks.</EmptyState>
          ) : (
            <Grid data={data} />
          )}
        </Panel>
      }
      side={data?.hasData ? <ThisWeek data={data} /> : undefined}
    />
  );
}
